import { ConfigStateService, CoreModule, LocalizationService } from '@abp/ng.core';
import { ThemeSharedModule, ToasterService } from '@abp/ng.theme.shared';
import { PermissionsService } from '@abp/ng.permission-management/proxy';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IdentityUserService } from '@volo/abp.ng.identity/proxy';
import type { IdentityUserDto, IdentityUserUpdateDto } from '@volo/abp.ng.identity/proxy';
import { finalize } from 'rxjs/operators';

/** Módulo que o diálogo não mostra: é recurso do admin, não da equipe (Vinícius 14/08). */
const MODULO_OCULTO = 'cargo_fleet.WhatsAppContacts';

/** Única filha com interruptor próprio: sem ela, Visibility é só acompanhar. */
const FUNCAO_AVANCAR_ETAPA = 'cargo_fleet.Viagens.AvancarEtapa';

/**
 * Ações que este diálogo NUNCA concede a um usuário — são do admin da plataforma. O payload as
 * manda explicitamente como isGranted:false para limpar concessão antiga.
 */
const FUNCOES_SO_DO_ADMIN = [
  'cargo_fleet.FleetScore.Recalcular',
  'cargo_fleet.FleetScore.ImportarFechamentoOficial',
  // Liga/desliga o robô de ocorrências para a empresa inteira: fica com o papel admin do tenant.
  'cargo_fleet.Ocorrencias.ConfigurarAutomacao',
];

/** Uma função (permissão-filha) de um módulo. Só a de avançar etapa aparece na tela. */
interface FuncaoAcesso {
  nome: string;
  concedida: boolean;
}

/** Um módulo (permissão-pai do grupo CargoFleet): um único interruptor. */
interface ModuloAcesso {
  nome: string;
  displayName: string;
  descricao: string;
  concedido: boolean;
  funcoes: FuncaoAcesso[];
  /** Filha renderizada com interruptor próprio (hoje só "pode avançar etapa"). */
  avancarEtapa: FuncaoAcesso | null;
}

/**
 * Aba "Usuários e Acessos" do Minha conta — a gestão de usuários do tenant na visão do
 * admin da transportadora (decisão do Ronan 14/08): simples, no padrão do sistema, sem a
 * densidade do Identity Pro. Criar, ativar/desativar, redefinir senha e conceder acessos
 * (diálogo nativo de permissões, que só mostra os módulos contratados via RequireFeatures).
 * Sem exclusão na v1: desativar cobre e não perde histórico.
 */
@Component({
  selector: 'app-usuarios-acessos',
  standalone: true,
  imports: [CoreModule, ThemeSharedModule, FormsModule, ReactiveFormsModule],
  templateUrl: './usuarios-acessos.component.html',
})
export class UsuariosAcessosComponent implements OnInit {
  private readonly userService = inject(IdentityUserService);
  private readonly configState = inject(ConfigStateService);
  private readonly toaster = inject(ToasterService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  usuarios: IdentityUserDto[] = [];
  carregando = false;
  filtro = '';

  // modal de criação
  criando = false;
  salvandoNovo = false;
  formNovo = this.fb.nonNullable.group({
    userName: ['', Validators.required],
    name: [''],
    surname: [''],
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });

  // modal de senha
  usuarioSenha: IdentityUserDto | null = null;
  redefinindoSenha = false;
  formSenha = this.fb.nonNullable.group({
    novaSenha: ['', Validators.required],
  });

  // diálogo simplificado de acessos (decisão do Ronan 14/08): SÓ o grupo CargoFleet, com os
  // nomes dos módulos — o diálogo nativo do ABP era impossível para o usuário de transportadora.
  private readonly permissionsService = inject(PermissionsService);
  private readonly localization = inject(LocalizationService);
  acessosVisivel = false;
  acessosUsuario: IdentityUserDto | null = null;
  carregandoAcessos = false;
  salvandoAcessos = false;
  modulos: ModuloAcesso[] = [];

  get meuId(): string | undefined {
    return this.configState.getDeep('currentUser')?.id;
  }

  ngOnInit(): void {
    this.carregar();
  }

  carregar(): void {
    this.carregando = true;
    this.userService
      .getList({ filter: this.filtro || undefined, maxResultCount: 100, skipCount: 0 })
      .pipe(finalize(() => {
        this.carregando = false;
        this.cdr.markForCheck();
      }))
      .subscribe(res => (this.usuarios = res.items ?? []));
  }

  abrirNovo(): void {
    this.formNovo.reset();
    this.criando = true;
  }

  salvarNovo(): void {
    if (this.formNovo.invalid) {
      this.formNovo.markAllAsTouched();
      return;
    }

    const valores = this.formNovo.getRawValue();
    this.salvandoNovo = true;
    this.userService
      .create({
        userName: valores.userName,
        name: valores.name,
        surname: valores.surname,
        email: valores.email,
        password: valores.password,
        isActive: true,
        // Senha definida pelo admin é provisória: o colega troca no primeiro login.
        shouldChangePasswordOnNextLogin: true,
        lockoutEnabled: true,
        roleNames: [],
        organizationUnitIds: [],
        sendConfirmationEmail: false,
        emailConfirmed: false,
        phoneNumberConfirmed: false,
        extraProperties: {},
      })
      .pipe(finalize(() => {
        this.salvandoNovo = false;
        this.cdr.markForCheck();
      }))
      .subscribe(() => {
        this.criando = false;
        this.toaster.success('::UsuariosAcessos:UsuarioCriado');
        this.carregar();
      });
  }

  alternarAtivo(usuario: IdentityUserDto): void {
    // O update do ABP exige o DTO completo + concurrencyStamp: busca antes de gravar.
    this.userService.get(usuario.id!).subscribe(atual => {
      this.userService
        .update(usuario.id!, <IdentityUserUpdateDto>{
          userName: atual.userName!,
          name: atual.name,
          surname: atual.surname,
          email: atual.email!,
          phoneNumber: atual.phoneNumber,
          isActive: !atual.isActive,
          // Preserva a troca obrigatória pendente: um recém-criado desativado e reativado antes do 1º
          // login continuaria obrigado a trocar a senha provisória (false aqui apagava isso).
          shouldChangePasswordOnNextLogin: atual.shouldChangePasswordOnNextLogin ?? false,
          lockoutEnabled: atual.lockoutEnabled ?? true,
          // Sem roleNames/organizationUnitIds de propósito: no update do ABP, lista NÃO nula vira
          // SetRolesAsync — e [] apagaria as roles (um admin desativado e reativado perderia a role
          // admin em silêncio). Ausente chega null e o ABP não mexe. No create o [] é intencional.
          emailConfirmed: atual.emailConfirmed ?? false,
          phoneNumberConfirmed: atual.phoneNumberConfirmed ?? false,
          concurrencyStamp: atual.concurrencyStamp,
          extraProperties: atual.extraProperties ?? {},
        })
        .subscribe(() => {
          this.toaster.success(
            atual.isActive ? '::UsuariosAcessos:UsuarioDesativado' : '::UsuariosAcessos:UsuarioAtivado',
          );
          this.carregar();
        });
    });
  }

  abrirSenha(usuario: IdentityUserDto): void {
    this.usuarioSenha = usuario;
    this.formSenha.reset();
  }

  salvarSenha(): void {
    if (!this.usuarioSenha || this.formSenha.invalid) {
      this.formSenha.markAllAsTouched();
      return;
    }

    this.redefinindoSenha = true;
    this.userService
      .updatePassword(this.usuarioSenha.id!, { newPassword: this.formSenha.getRawValue().novaSenha })
      .pipe(finalize(() => {
        this.redefinindoSenha = false;
        this.cdr.markForCheck();
      }))
      .subscribe(() => {
        this.usuarioSenha = null;
        this.toaster.success('::UsuariosAcessos:SenhaRedefinida');
      });
  }

  abrirAcessos(usuario: IdentityUserDto): void {
    this.acessosUsuario = usuario;
    this.modulos = [];
    this.carregandoAcessos = true;
    this.acessosVisivel = true;

    // O backend já entrega o grupo filtrado por contrato (RequireFeatures): módulo que o tenant
    // não contratou nem chega aqui. providerName 'U' = concessão direta ao usuário.
    // A ORDEM dos cards é a ordem que o backend devolve (cargo_fleetPermissionDefinitionProvider):
    // não ordene aqui, ou a tela deixa de refletir a decisão de produto.
    this.permissionsService
      .get('U', usuario.id!)
      .pipe(finalize(() => {
        this.carregandoAcessos = false;
        this.cdr.markForCheck();
      }))
      .subscribe(resultado => {
        const grupo = resultado.groups?.find(g => g.name === 'cargo_fleet');
        const permissoes = grupo?.permissions ?? [];

        this.modulos = permissoes
          .filter(p => !p.parentName && p.name !== MODULO_OCULTO)
          .map(pai => {
            const filhas = permissoes
              .filter(filha => filha.parentName === pai.name)
              .map(filha => ({ nome: filha.name!, concedida: filha.isGranted }));

            return {
              nome: pai.name!,
              displayName: pai.displayName ?? pai.name!,
              descricao: this.descricaoDe(pai.name!),
              concedido: pai.isGranted,
              funcoes: filhas,
              avancarEtapa: filhas.find(f => f.nome === FUNCAO_AVANCAR_ETAPA) ?? null,
            };
          });
      });
  }

  /** Frase de uma linha por módulo. Sem chave cadastrada, o card fica só com o nome. */
  private descricaoDe(nomeDaPermissao: string): string {
    const curto = nomeDaPermissao.replace('cargo_fleet.', '');
    return this.localization.instant({ key: `::UsuariosAcessos:Desc:${curto}`, defaultValue: '' });
  }

  /** Módulo é um interruptor só: ligar libera tudo o que se faz nele (o save aplica as exceções). */
  alternarModulo(modulo: ModuloAcesso): void {
    modulo.concedido = !modulo.concedido;
  }

  salvarAcessos(): void {
    if (!this.acessosUsuario) return;

    const permissions = this.modulos.flatMap(modulo => [
      { name: modulo.nome, isGranted: modulo.concedido },
      ...modulo.funcoes.map(funcao => ({ name: funcao.nome, isGranted: this.concede(modulo, funcao) })),
    ]);

    this.salvandoAcessos = true;
    this.permissionsService
      .update('U', this.acessosUsuario.id!, { permissions })
      .pipe(finalize(() => {
        this.salvandoAcessos = false;
        this.cdr.markForCheck();
      }))
      .subscribe(() => {
        this.acessosVisivel = false;
        this.toaster.success('::UsuariosAcessos:AcessosSalvos');
      });
  }

  private concede(modulo: ModuloAcesso, funcao: FuncaoAcesso): boolean {
    if (FUNCOES_SO_DO_ADMIN.includes(funcao.nome)) return false;
    if (funcao.nome === FUNCAO_AVANCAR_ETAPA) return modulo.concedido && funcao.concedida;
    return modulo.concedido;
  }
}
